import { useMemo, useState } from 'react';
import { MapPinOff, Trash2, Plus, Pencil } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DataTable } from '@/components/glitch-ui/data-table';
import { IconButton } from '@/components/glitch-ui/icon-button';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { CountryRegionPicker } from '@/components/business/business-locations/country-region-picker';
import { LocationMapPicker } from '@/components/business/business-locations/location-map-picker';
import { locationsByIdsQueryOptions } from '@/lib/query-options';
import { formatReach } from '@/lib/utils';
import type { ExcludedLocation, Location } from '@/lib/types';

interface ExcludedLocationsSectionProps {
  /** Current excluded (negative) locations — staged in the campaign draft. */
  excludedLocations: ExcludedLocation[];
  /** Stage a new excluded set; persisted by the campaign page's Save Changes (not immediately). */
  onChange: (next: ExcludedLocation[]) => void;
  disabled?: boolean;
  /** Business id for the map picker's geocoding/boundaries. Falls back to the
   * list-based CountryRegionPicker when absent. */
  businessId?: number;
}

const formatTargetType = (targetType: string): string =>
  targetType.charAt(0).toUpperCase() + targetType.slice(1).toLowerCase();

const getTargetTypePillStyle = (targetType: string) => {
  switch (targetType.toLowerCase()) {
    case 'country':
      return 'bg-purple-50 text-purple-600';
    case 'region':
    case 'state':
      return 'bg-blue-50 text-blue-600';
    case 'city':
      return 'bg-green-50 text-green-600';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

const formatCanonicalName = (name: string): string => name.replace(/,/g, ', ');

// An exclusion is just a geo Location reference (criteria_id + display fields).
const locationToExcluded = (l: Location): ExcludedLocation => ({
  criteria_id: l.criteria_id,
  name: l.name,
  canonical_name: l.canonical_name,
  country_code: l.country_code,
  target_type: l.target_type,
});

export function ExcludedLocationsSection({
  excludedLocations,
  onChange,
  disabled = false,
  businessId,
}: ExcludedLocationsSectionProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleting, setDeleting] = useState<ExcludedLocation | null>(null);
  const [modalSelected, setModalSelected] = useState<Location[]>([]);
  // Set while editing a row: the modal opens seeded with that exclusion's location, and on save
  // the original is dropped if it was swapped out.
  const [editing, setEditing] = useState<ExcludedLocation | null>(null);

  const excluded = excludedLocations;

  // Excluded rows carry no reach; resolve it from the geo catalog by criteria_id (#5481).
  const excludedCriteriaIds = useMemo(() => excluded.map((e) => e.criteria_id), [excluded]);
  const { data: reachLocations } = useQuery(locationsByIdsQueryOptions(excludedCriteriaIds));
  const reachById = useMemo(() => {
    const m = new Map<number, number>();
    for (const loc of reachLocations ?? []) m.set(loc.criteria_id, loc.reach);
    return m;
  }, [reachLocations]);

  const columns: ColumnDef<ExcludedLocation>[] = [
    {
      accessorKey: 'canonical_name',
      header: 'Location',
      // Location fills; Type/Reach are fixed-width to match the campaign Locations table above.
      meta: { className: 'px-6' },
      cell: ({ row }) => (
        <div className='flex items-center gap-3'>
          <MapPinOff className='h-4 w-4 text-gray-500' />
          <span className='text-sm font-medium text-gray-900'>{formatCanonicalName(row.original.canonical_name)}</span>
        </div>
      ),
    },
    {
      accessorKey: 'target_type',
      header: 'Type',
      // w-32 to match the campaign Locations table's Type column.
      meta: { className: 'w-32 px-6' },
      cell: ({ row }) => (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTargetTypePillStyle(
            row.original.target_type
          )}`}
        >
          {formatTargetType(row.original.target_type)}
        </span>
      ),
    },
    {
      id: 'reach',
      header: 'Reach',
      // Sort numerically; rows without a reach ("-") use -1 so they sink to the bottom.
      accessorFn: (row) => reachById.get(row.criteria_id) ?? -1,
      meta: { className: 'w-20' },
      cell: ({ row }) => (
        <span className='text-sm tabular-nums text-gray-700'>{formatReach(reachById.get(row.original.criteria_id))}</span>
      ),
    },
    {
      id: 'actions',
      header: () => null,
      enableSorting: false,
      meta: { className: 'w-[72px] px-3 py-3' },
      cell: ({ row }) =>
        !disabled ? (
          <div className='flex items-center justify-end gap-0.5'>
            <IconButton aria-label='Edit' title='Edit' onClick={() => openEdit(row.original)}>
              <Pencil className='h-4 w-4' />
            </IconButton>
            <IconButton variant='destructive' aria-label='Delete' onClick={() => setDeleting(row.original)}>
              <Trash2 className='h-4 w-4' />
            </IconButton>
          </div>
        ) : null,
    },
  ];

  // Rebuild a Location from an exclusion to pre-select it in the picker when editing
  // (reach from the catalog lookup; parent_id/status are display-only here).
  const excludedToLocation = (e: ExcludedLocation): Location => ({
    criteria_id: e.criteria_id,
    canonical_name: e.canonical_name,
    country_code: e.country_code,
    name: e.name,
    parent_id: 0,
    reach: reachById.get(e.criteria_id) ?? 0,
    status: 'Active',
    target_type: e.target_type,
  });

  const openEdit = (row: ExcludedLocation) => {
    setEditing(row);
    setModalSelected([excludedToLocation(row)]);
    setIsModalOpen(true);
  };

  // Stage the modal selection into the draft (persisted by the page's Save Changes).
  const handleAddSelected = () => {
    if (editing) {
      // Per-row edit: the modal was seeded with just this exclusion — swap it (add the new pick,
      // drop the original if it was deselected) without touching the other exclusions.
      const existingIds = new Set(excluded.map((e) => e.criteria_id));
      const selectedIds = new Set(modalSelected.map((l) => l.criteria_id));
      const additions = modalSelected.filter((l) => !existingIds.has(l.criteria_id)).map(locationToExcluded);
      let next = [...excluded, ...additions];
      if (!selectedIds.has(editing.criteria_id)) {
        next = next.filter((e) => e.criteria_id !== editing.criteria_id);
      }
      onChange(next);
    } else {
      // Add modal: seeded with all current exclusions, so the selection IS the new full set.
      onChange(modalSelected.map(locationToExcluded));
    }
    setModalSelected([]);
    setEditing(null);
    setIsModalOpen(false);
  };

  const handleDeleteConfirm = () => {
    if (!deleting) return;
    onChange(excluded.filter((e) => e.criteria_id !== deleting.criteria_id));
    setDeleting(null);
  };

  return (
    <div>
      <div className='flex items-center justify-between mb-4'>
        <div>
          <h3 className='text-lg font-semibold text-gray-900'>Excluded Locations</h3>
          <p className='text-sm text-gray-700 mt-1'>Stop ads from showing in specific geographic areas.</p>
        </div>
        {!disabled && (
          <Button
            variant='outline'
            className='text-gray-900'
            onClick={() => {
              setEditing(null);
              // Seed with the current exclusions so they show in the picker; add/remove from there.
              setModalSelected(excluded.map(excludedToLocation));
              setIsModalOpen(true);
            }}
          >
            <Plus className='h-4 w-4 mr-2' />
            Add Exclusion
          </Button>
        )}
      </div>

      <DataTable
        data={excluded}
        columns={columns}
        variant='bordered'
        pageSize={999}
        initialSorting={[{ id: 'canonical_name', desc: false }]}
        emptyIcon={MapPinOff}
        emptyTitle='No Location Exclusions'
        emptyDescription='Add locations to stop ads from showing in specific geographic areas.'
      />

      {/*
        Add Exclusion modal: the same map picker as the Add Location modal, but
        geo-only (`catalogOnly` hides the Radius tab). Exclusions are geo `Location`
        rows (criteria_id only) — radius/Worldwide (BusinessLocation variants) don't
        apply. Falls back to the list-based CountryRegionPicker when there's no
        business (the picker needs one for geocoding/boundaries).
      */}
      <Dialog
        open={isModalOpen}
        onOpenChange={(open) => {
          setIsModalOpen(open);
          if (!open) setEditing(null);
        }}
      >
        <DialogContent
          className='flex h-[85vh] max-h-[85vh] w-[92vw] flex-col gap-0 overflow-hidden p-0 sm:max-w-[1100px] [&>button]:top-4'
          aria-describedby={undefined}
        >
          <DialogHeader className='border-b border-gray-200 px-5 py-4'>
            <DialogTitle>{editing ? 'Edit Exclusion' : 'Add Exclusion'}</DialogTitle>
          </DialogHeader>

          <div className='min-h-0 flex-1 overflow-hidden'>
            {businessId != null ? (
              <LocationMapPicker
                businessId={businessId}
                catalogOnly
                intent="exclude"
                selected={modalSelected}
                onChange={setModalSelected}
                excludedCriteriaIds={new Set(excludedCriteriaIds)}
              />
            ) : (
              <div className='h-full overflow-y-auto p-5'>
                <CountryRegionPicker selected={modalSelected} onChange={setModalSelected} />
              </div>
            )}
          </div>

          <div className='flex justify-end gap-2 border-t border-gray-200 px-5 py-3'>
            <Button
              variant='outline'
              onClick={() => {
                setIsModalOpen(false);
                setEditing(null);
              }}
            >
              Cancel
            </Button>
            <Button className='bg-[#290BD9] text-white hover:bg-[#2309C5]' onClick={handleAddSelected}>
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConformationModal
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={handleDeleteConfirm}
        title='Remove Exclusion'
        description={`Remove "${deleting ? formatCanonicalName(deleting.canonical_name) : ''}" from location exclusions?`}
        confirmText='Remove'
        cancelText='Cancel'
        isLoading={false}
      />
    </div>
  );
}
